import {createApi} from '@reduxjs/toolkit/query/react';

import {omneticApi, chatBaseQuery} from '@dms/api/base';

import {
  GetConversationDetailParams,
  GetConversationDetailResponse,
  getConversationDetailParams,
  getConversationDetailResponse,
  GetConversationEventsParams,
  GetConversationEventsResponse,
  getConversationEventsParams,
  getConversationEventsResponse,
  ReadAllConversationEventsParams,
  readAllConversationEventsParams,
  SendEventAsPublicUserParams,
  SendEventAsPublicUserBody,
  sendEventAsPublicUserParams,
  sendEventAsPublicUserBody,
  GetManyConversationEventsResponse,
  GetManyConversationEventsParams,
  getManyConversationEventsParams,
  getManyConversationEventsResponse,
} from '@chat/shared/validations';

import {
  chatSettingsSchema,
  GetChatSettingsApiArg,
  GetChatSettingsApiResponse,
  PutChatSettingsApiArg,
  PutChatSettingsApiResponse,
  GetBusinessCaseMessageChannelApiResponse,
  GetBusinessCaseMessageChannelApiArg,
  SetBusinessCaseMessageChannelApiResponse,
  SetBusinessCaseMessageChannelApiArg,
  GetInterestMessageChannelApiResponse,
  GetInterestMessageChannelApiArg,
  SetInterestMessageChannelApiResponse,
  SetInterestMessageChannelApiArg,
  SubscribeToChatConversationApiArg,
  SubscribeToChatConversationApiResponse,
  UnsubscribeFromChatConversationApiArg,
  UnsubscribeFromChatConversationApiResponse,
} from './types';

export const chatAgentApi = createApi({
  reducerPath: 'chatAgentApi',
  baseQuery: chatBaseQuery,
  tagTypes: ['ConversationEvents', 'Conversation'],
  endpoints: (build) => ({
    getAgentEvents: build.query<GetConversationEventsResponse, GetConversationEventsParams>({
      query: ({conversationId}) => ({
        url: `/agent/api/v2/conversation/${conversationId}/events`,
        method: 'GET',
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'ConversationEvents', conversationId: queryArg.conversationId},
      ],
      extraOptions: {
        requestSchema: getConversationEventsParams,
        responseSchema: getConversationEventsResponse,
      },
    }),
    getManyAgentEvents: build.query<
      GetManyConversationEventsResponse,
      GetManyConversationEventsParams
    >({
      query: ({conversationIds}) => ({
        url: `/agent/api/v2/conversations/events`,
        params: {conversationIds},
        method: 'GET',
      }),
      providesTags: (result, error, queryArg) =>
        queryArg.conversationIds.map((conversationId) => ({
          type: 'ConversationEvents',
          conversationId,
        })),
      extraOptions: {
        requestSchema: getManyConversationEventsParams,
        responseSchema: getManyConversationEventsResponse,
      },
    }),
    createAgentEvent: build.mutation<void, SendEventAsPublicUserParams & SendEventAsPublicUserBody>(
      {
        query: ({conversationId, content}) => ({
          url: `/agent/api/v2/conversation/${conversationId}/events`,
          method: 'POST',
          body: {content},
        }),
        invalidatesTags: (result, error, queryArg) => [
          {type: 'ConversationEvents', conversationId: queryArg.conversationId},
        ],
        extraOptions: {
          requestSchema: sendEventAsPublicUserParams.merge(sendEventAsPublicUserBody),
        },
      }
    ),
    markAgentEventsAsRead: build.mutation<void, ReadAllConversationEventsParams>({
      query: ({conversationId}) => ({
        url: `/agent/api/v2/conversation/${conversationId}/events/read-all`,
        method: 'POST',
      }),
      extraOptions: {
        requestSchema: readAllConversationEventsParams,
      },
    }),
    getAgentConversation: build.query<GetConversationDetailResponse, GetConversationDetailParams>({
      query: ({conversationId}) => ({
        url: `/agent/api/v2/conversation/${conversationId}`,
        method: 'GET',
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'Conversation', conversationId: queryArg.conversationId},
      ],
      extraOptions: {
        requestSchema: getConversationDetailParams,
        responseSchema: getConversationDetailResponse,
      },
    }),
  }),
});

const injectedRtkApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getChatSettings: build.query<GetChatSettingsApiResponse, GetChatSettingsApiArg>({
      query: () => ({
        url: `/dms/v1/settings/chat`,
      }),
      providesTags: ['ChatSettings'],
      extraOptions: {
        responseSchema: chatSettingsSchema,
      },
    }),
    putChatSettings: build.mutation<PutChatSettingsApiResponse, PutChatSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/settings/chat`,
        method: 'PUT',
        body: queryArg.chatSettingsRequestBody,
      }),
      invalidatesTags: ['ChatSettings'],
      extraOptions: {
        requestSchema: chatSettingsSchema,
      },
    }),
    subscribeToChatConversation: build.mutation<
      SubscribeToChatConversationApiResponse,
      SubscribeToChatConversationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/chat/${queryArg.conversationId}/subscribe`,
        method: 'POST',
      }),
    }),
    unsubscribeFromChatConversation: build.mutation<
      UnsubscribeFromChatConversationApiResponse,
      UnsubscribeFromChatConversationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/chat/${queryArg.conversationId}/unsubscribe`,
        method: 'POST',
      }),
    }),
    getBusinessCaseMessageChannel: build.query<
      GetBusinessCaseMessageChannelApiResponse,
      GetBusinessCaseMessageChannelApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/business-case/${queryArg.businessCaseId}/message-channel`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'BusinessCaseMessageChannel', businessCaseId: queryArg.businessCaseId},
      ],
    }),
    setBusinessCaseMessageChannel: build.mutation<
      SetBusinessCaseMessageChannelApiResponse,
      SetBusinessCaseMessageChannelApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/business-case/${queryArg.businessCaseId}/message-channel`,
        method: 'POST',
        body: queryArg.setMessageChannelRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'BusinessCaseMessageChannel', businessCaseId: queryArg.businessCaseId},
      ],
    }),
    getInterestMessageChannel: build.query<
      GetInterestMessageChannelApiResponse,
      GetInterestMessageChannelApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/interest/${queryArg.interestId}/message-channel`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'InterestMessageChannel', interestId: queryArg.interestId},
      ],
    }),
    setInterestMessageChannel: build.mutation<
      SetInterestMessageChannelApiResponse,
      SetInterestMessageChannelApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/interest/${queryArg.interestId}/message-channel`,
        method: 'POST',
        body: queryArg.setMessageChannelRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'InterestMessageChannel', interestId: queryArg.interestId},
      ],
    }),
  }),
});

export {injectedRtkApi as chatApi};

export const {
  useGetChatSettingsQuery,
  usePutChatSettingsMutation,
  useGetBusinessCaseMessageChannelQuery,
  useSetBusinessCaseMessageChannelMutation,
  useGetInterestMessageChannelQuery,
  useSetInterestMessageChannelMutation,
  useSubscribeToChatConversationMutation,
  useUnsubscribeFromChatConversationMutation,
} = injectedRtkApi;
